/**
 * Workflow API Client
 * Dedicated axios client for workflow service with 401 interceptor
 * Triggers re-auth modal on token expiry instead of "Failed to fetch"
 */

import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { handle401WithSilentRefresh } from './authRefresh';
import {
  applyValidatedExplicitOrganizationHeaders,
  getValidatedExplicitOrganizationHeaderValues,
  getValidatedStoredOrganizationHeaderValues,
  applyValidatedStoredOrganizationHeaders,
  hasExplicitOrganizationHeaders,
  omitReservedOrganizationHeaders,
} from './client';
import {
  buildWorkflowOrganizationApiRoute,
  buildWorkflowUserApiRoute,
  getCurrentWorkflowUserId,
  getValidatedCurrentWorkflowOrganization,
} from '@/utils/workflow-utils';
import { getCurrentAuthTokens } from './auth-session';

// =============================================================================
// Workflow Creation Types
// =============================================================================

/**
 * Workflow node definition for workflow creation
 */
export interface WorkflowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

/**
 * Workflow edge connecting nodes
 */
export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}

/**
 * Workflow trigger definition
 */
export interface WorkflowTrigger {
  id: string;
  type: string;
  config?: Record<string, unknown>;
  enabled: boolean;
}

/**
 * Workflow variable definition
 */
export interface WorkflowVariable {
  name: string;
  type: string;
  required?: boolean;
  description?: string;
}

/**
 * Source info for workflows created from forms/pages
 * Enables back-navigation from workflow editor to the source page
 */
export interface WorkflowSourceInfo {
  type: 'form';
  pageId: string;
  pageName?: string;
  componentId: string;
  componentLabel?: string;
  projectId?: string;
}

/**
 * Request payload for creating a workflow
 */
export interface CreateWorkflowRequest {
  name: string;
  description?: string;
  definition: {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
    variables?: WorkflowVariable[];
    triggers?: WorkflowTrigger[];
  };
  status?: 'draft' | 'published';
  tags?: string[];
  metadata?: {
    customFields?: Record<string, unknown>;
    [key: string]: unknown;
  };
  /** Optional source info for auto-generated workflows (enables back-navigation) */
  sourceInfo?: WorkflowSourceInfo;
}

/**
 * Response from workflow creation API
 * Note: Workflow model transforms _id to id in toJSON serialization
 */
export interface CreateWorkflowResponse {
  success: boolean;
  data?: { id: string; _id?: string; name: string };
  error?: string;
}

export interface WorkflowSecretMetadata {
  id: string;
  name: string;
  description?: string;
  owner_type: 'User' | 'Organization';
  owner_id: string;
  secretType: 'standard' | 'user_protected';
  keyVersion: number;
  source: 'ui' | 'workflow_node' | 'ai_chat';
  scope: 'user' | 'org';
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowSecretListResponse {
  data?: {
    secrets?: WorkflowSecretMetadata[];
  };
}

export interface WorkflowListItem {
  _id: string;
  id?: string;
  name: string;
  description?: string;
  status: 'draft' | 'published';
  type: string;
  executionCount?: number;
  lastExecutedAt?: string;
  updatedAt?: string;
  createdAt?: string;
}

export interface WorkflowListApiResponse {
  data?: WorkflowListItem[] | { workflows?: WorkflowListItem[] };
  workflows?: WorkflowListItem[];
}

export interface WorkflowVersionEntry {
  _id: string;
  workflowId: string;
  version: number;
  changeType: 'initial' | 'agent_request' | 'supervisor_fix' | 'user_edit' | 'rollback';
  requestedBy: 'agent' | 'supervisor' | 'user' | 'system';
  reason: string;
  changeDescription?: string;
  previousVersion?: number;
  executionMetrics?: {
    successRate?: number;
    avgExecutionTime?: number;
    llmCallsPerCycle?: number;
    executionsSinceVersion?: number;
  };
  createdAt: string;
}

export interface WorkflowVersionListResponse {
  data?: WorkflowVersionEntry[];
}

// Extend axios request config for retry tracking
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

// Workflow service base URL
// Note: Default port 8001 matches workflow service; override with VITE_WORKFLOW_BACKEND_URL
const WORKFLOW_API_BASE = import.meta.env.VITE_WORKFLOW_BACKEND_URL || '/workflow-api';

const normalizeWorkflowBaseUrl = (value: string): string =>
  value.endsWith('/') ? value : `${value}/`;

export const getWorkflowApiBaseUrl = (): string =>
  (() => {
    const normalizedBaseUrl = normalizeWorkflowBaseUrl(WORKFLOW_API_BASE);
    if (/^https?:\/\//i.test(normalizedBaseUrl)) {
      return normalizedBaseUrl;
    }

    if (typeof window !== 'undefined') {
      return new URL(normalizedBaseUrl, window.location.origin).toString();
    }

    return normalizedBaseUrl;
  })();

const isAbsoluteWorkflowUrl = (value: string): boolean =>
  /^https?:\/\//i.test(value);

const WORKFLOW_ORG_ROUTE_PATTERN = /(^|\/)api\/orgs\/([^/?#]+)(?=\/|$)/;
type WorkflowHeadersInit = ConstructorParameters<typeof Headers>[0];
type WorkflowRequestInit = NonNullable<Parameters<typeof fetch>[1]>;

const canonicalizeWorkflowOrganizationPath = (path: string): string => {
  const currentOrganization = getValidatedCurrentWorkflowOrganization();
  if (!currentOrganization) {
    return path;
  }

  return path.replace(
    WORKFLOW_ORG_ROUTE_PATTERN,
    (match, prefix: string, slug: string) =>
      slug === currentOrganization.slug
        ? match
        : `${prefix}api/orgs/${currentOrganization.slug}`,
  );
};

const trimLeadingAndTrailingSlashes = (value: string): string =>
  value.replace(/^\/+|\/+$/g, '');

const stripRedundantWorkflowBasePrefix = (
  path: string,
  requestBaseUrl: string,
): string => {
  try {
    const parsedBase = new URL(
      requestBaseUrl,
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost',
    );
    const basePath = trimLeadingAndTrailingSlashes(parsedBase.pathname);
    if (!basePath) {
      return path;
    }

    return path.startsWith(`${basePath}/`)
      ? path.slice(basePath.length + 1)
      : path;
  } catch {
    return path;
  }
};

const resolveWorkflowRequestUrl = (
  path: string,
  baseUrl?: string,
): string => {
  const trimmedPath = path.trim();

  if (isAbsoluteWorkflowUrl(trimmedPath)) {
    try {
      const absoluteUrl = new URL(trimmedPath);
      absoluteUrl.pathname = canonicalizeWorkflowOrganizationPath(
        absoluteUrl.pathname,
      );
      return absoluteUrl.toString();
    } catch {
      return trimmedPath;
    }
  }

  const canonicalPath = canonicalizeWorkflowOrganizationPath(trimmedPath);
  const normalizedPath = canonicalPath.replace(/^\/+/, '');
  const requestBaseUrl = baseUrl
    ? normalizeWorkflowBaseUrl(baseUrl)
    : getWorkflowApiBaseUrl();
  const normalizedPathWithoutBasePrefix = stripRedundantWorkflowBasePrefix(
    normalizedPath,
    requestBaseUrl,
  );

  return new URL(normalizedPathWithoutBasePrefix, requestBaseUrl).toString();
};

const headersInitToRecord = (headers?: WorkflowHeadersInit): Record<string, string> => {
  const normalizedHeaders = new Headers(headers);
  return Object.fromEntries(normalizedHeaders.entries());
};

export const getWorkflowRequestHeaders = async (options?: {
  headers?: WorkflowHeadersInit;
  organizationId?: string | null;
  organizationSlug?: string | null;
}): Promise<Record<string, string>> => {
  let headers = omitReservedOrganizationHeaders(
    headersInitToRecord(options?.headers),
  );
  const tokens = getCurrentAuthTokens();
  if (tokens?.accessToken) {
    headers.Authorization = `Bearer ${tokens.accessToken}`;
  }

  let organizationHeaders: Record<string, string> = {};

  if (options?.organizationId || options?.organizationSlug) {
    organizationHeaders = await getValidatedExplicitOrganizationHeaderValues({
      organizationId: options.organizationId,
      organizationSlug: options.organizationSlug,
    });
  } else {
    const currentOrganization = getValidatedCurrentWorkflowOrganization();
    organizationHeaders = currentOrganization
      ? {
          'X-Organization-Context': currentOrganization.slug,
          'X-Organization-Id': currentOrganization._id,
        }
      : await getValidatedStoredOrganizationHeaderValues();
  }

  return {
    ...headers,
    ...organizationHeaders,
  };
};

export const workflowFetch = async (
  path: string,
  options?: Omit<WorkflowRequestInit, 'headers'> & {
    headers?: WorkflowHeadersInit;
    baseUrl?: string;
    organizationId?: string | null;
    organizationSlug?: string | null;
  },
): Promise<Response> => {
  const {
    baseUrl,
    organizationId,
    organizationSlug,
    headers,
    ...requestOptions
  } = options ?? {};

  return fetch(resolveWorkflowRequestUrl(path, baseUrl), {
    ...requestOptions,
    headers: await getWorkflowRequestHeaders({
      headers,
      organizationId,
      organizationSlug,
    }),
  });
};

export const buildValidatedWorkflowRoute = (resource: string): string => {
  const normalizedResource = resource.replace(/^\/+/, '');
  const currentOrganization = getValidatedCurrentWorkflowOrganization();
  if (currentOrganization) {
    return buildWorkflowOrganizationApiRoute(
      currentOrganization.slug,
      normalizedResource,
    );
  }

  return buildWorkflowUserApiRoute(
    normalizedResource,
    getCurrentWorkflowUserId(),
  );
};

/**
 * Create workflow API client with 401 handling
 */
export const createWorkflowClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: WORKFLOW_API_BASE,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add auth token
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const tokens = getCurrentAuthTokens();
      if (tokens?.accessToken) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }

      const hasPerRequestOrgOverride =
        hasExplicitOrganizationHeaders(config.headers);

      if (hasPerRequestOrgOverride) {
        await applyValidatedExplicitOrganizationHeaders(config.headers);
      } else {
        await applyValidatedStoredOrganizationHeaders(config.headers);
      }

      return config;
    },
    (error) => {
      console.error('Workflow API request error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor — delegate 401 to the shared silent-refresh-then-
  // modal handler so workflow-service 401s recover identically to main
  // backend, agent-service, and data-service. A revoked-jti 401 used to
  // jump straight to the modal here; now it silently refreshes first.
  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        // Only handle 401s if we HAD a token that failed
        const hadToken = getCurrentAuthTokens();
        if (hadToken?.accessToken) {
          originalRequest._retry = true;
          try {
            return await handle401WithSilentRefresh(error, client);
          } catch {
            // Refresh-then-modal exhausted — fall through to the normalized
            // ApiError below so the caller gets a structured rejection.
          }
        }
      }

      const apiError = {
        code: error.response?.data?.code || 'WORKFLOW_API_ERROR',
        message: error.response?.data?.message || error.message || 'Workflow API error',
        status: error.response?.status,
        details: error.response?.data?.details,
      };

      return Promise.reject(apiError);
    }
  );

  return client;
};

// Create the workflow client instance
const workflowClient = createWorkflowClient();

/**
 * Workflow API helper methods
 * Provides typed request methods for workflow service
 */
export const workflowApi = {
  /**
   * GET request to workflow service
   */
  get: async <T = unknown>(url: string): Promise<T> => {
    const response = await workflowClient.get<T>(url);
    return response.data;
  },

  /**
   * POST request to workflow service
   */
  post: async <T = unknown>(url: string, data?: unknown): Promise<T> => {
    const response = await workflowClient.post<T>(url, data);
    return response.data;
  },

  /**
   * PATCH request to workflow service
   */
  patch: async <T = unknown>(url: string, data?: unknown): Promise<T> => {
    const response = await workflowClient.patch<T>(url, data);
    return response.data;
  },

  /**
   * PUT request to workflow service
   */
  put: async <T = unknown>(url: string, data?: unknown): Promise<T> => {
    const response = await workflowClient.put<T>(url, data);
    return response.data;
  },

  /**
   * DELETE request to workflow service
   */
  delete: async <T = unknown>(url: string): Promise<T> => {
    const response = await workflowClient.delete<T>(url);
    return response.data;
  },

  /**
   * Create a new workflow via direct API
   * Used by FormBuilderAgent to create form submission workflows
   */
  createWorkflow: async (request: CreateWorkflowRequest): Promise<CreateWorkflowResponse> => {
    return workflowApi.post<CreateWorkflowResponse>('/api/workflows', request);
  },

  // Secrets Management
  listSecrets: async (scope?: string): Promise<WorkflowSecretListResponse> => {
    const params = scope ? `?scope=${scope}` : '';
    return workflowApi.get<WorkflowSecretListResponse>(`/api/secrets${params}`);
  },
  createSecret: async (data: { name: string; value: string; scope: string; secretType?: string; passphrase?: string; description?: string }): Promise<unknown> => {
    return workflowApi.post<unknown>('/api/secrets', data);
  },
  getSecret: async (id: string, scope?: string): Promise<unknown> => {
    const params = scope ? `?scope=${scope}` : '';
    return workflowApi.get<unknown>(`/api/secrets/${id}${params}`);
  },
  updateSecret: async (id: string, data: { value?: string; description?: string; passphrase?: string; scope: string }): Promise<unknown> => {
    return workflowApi.put<unknown>(`/api/secrets/${id}`, data);
  },
  deleteSecret: async (id: string, scope: string): Promise<unknown> => {
    return workflowApi.delete<unknown>(`/api/secrets/${id}?scope=${scope}`);
  },
  getSecretAudit: async (id?: string, scope?: string): Promise<unknown> => {
    const params = scope ? `?scope=${scope}` : '';
    if (id) {
      return workflowApi.get<unknown>(`/api/secrets/audit/${id}${params}`);
    }
    return workflowApi.get<unknown>(`/api/secrets/audit${params}`);
  },

  // Workflow listing with filters
  listWorkflows: async (params?: { type?: string; status?: string }): Promise<WorkflowListApiResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.set('type', params.type);
    if (params?.status) searchParams.set('status', params.status);
    const qs = searchParams.toString();
    return workflowApi.get<WorkflowListApiResponse>(`/api/workflows${qs ? `?${qs}` : ''}`);
  },

  // Workflow Version Snapshots (Agent Supervisor)
  getWorkflowVersions: async (workflowId: string, limit = 20, changeType?: string): Promise<WorkflowVersionListResponse> => {
    let url = `/api/workflow-versions?workflowId=${workflowId}&limit=${limit}`;
    if (changeType) url += `&changeType=${changeType}`;
    return workflowApi.get<WorkflowVersionListResponse>(url);
  },
  getWorkflowVersion: async (versionId: string): Promise<unknown> => {
    return workflowApi.get<unknown>(`/api/workflow-versions/${versionId}`);
  },

  /**
   * Execute a Data Source workflow synchronously and return the data.
   * Used by chart/table/gauge components that use workflows as their data source.
   */
  executeWorkflowDataSource: async (
    workflowId: string,
    triggerData?: Record<string, unknown>,
    timeoutMs?: number,
    traceHeaders?: { threadId: string; parentSpanId?: string; traceLevel: number }
  ): Promise<{
    success: boolean;
    data: Record<string, unknown>[];
    metadata?: { rowCount: number; executionTimeMs: number; cached?: boolean };
    error?: string;
  }> => {
    // Build trace headers for cross-service propagation
    const headers: Record<string, string> = {};
    if (traceHeaders) {
      headers['X-Trace-Thread-Id'] = traceHeaders.threadId;
      if (traceHeaders.parentSpanId) headers['X-Trace-Parent-Span-Id'] = traceHeaders.parentSpanId;
      headers['X-Trace-Level'] = String(traceHeaders.traceLevel);
    }

    const response = await workflowClient.post(`/api/workflows/${workflowId}/execute-sync`, {
      triggerData: triggerData || {},
      timeoutMs: timeoutMs || 30000,
    }, headers && Object.keys(headers).length > 0 ? { headers } : undefined);
    return response.data;
  },
};

export default workflowClient;
